// ConversationList - Liste aller Konversationen

import React, { useEffect, useState } from 'react';
import { MessageCircle, Search, Loader2 } from 'lucide-react';
import type { ConversationWithOther } from '../../types/messaging';
import { subscribeToConversations } from '../../services/messagingService';
import { Timestamp } from '@firebase/firestore';

interface ConversationListProps {
  userId: string;
  onSelectConversation: (conversationId: string, otherUserId: string) => void;
  selectedConversationId?: string;
}

export function ConversationList({
  userId,
  onSelectConversation,
  selectedConversationId,
}: ConversationListProps) {
  const [conversations, setConversations] = useState<ConversationWithOther[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const unsubscribe = subscribeToConversations(userId, (convs) => {
      setConversations(convs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const filteredConversations = conversations.filter((conv) =>
    conv.otherUser.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white"
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <MessageCircle className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">
              {searchQuery ? 'Keine Ergebnisse' : 'Noch keine Nachrichten'}
            </p>
            {!searchQuery && (
              <p className="text-sm text-gray-400">
                Besuche ein Profil um eine Unterhaltung zu starten
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                userId={userId}
                isSelected={conversation.id === selectedConversationId}
                onClick={() =>
                  onSelectConversation(conversation.id, conversation.otherUser.id)
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ConversationItemProps {
  conversation: ConversationWithOther;
  userId: string;
  isSelected: boolean;
  onClick: () => void;
}

function ConversationItem({
  conversation,
  userId,
  isSelected,
  onClick,
}: ConversationItemProps) {
  const unreadCount = conversation.unreadCount[userId] || 0;
  const hasUnread = unreadCount > 0;

  // Format timestamp
  const formatTime = (timestamp: Timestamp | Date | undefined) => {
    if (!timestamp) return '';

    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Gestern';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('de-DE', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
    }
  };

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-4 text-left transition-colors hover:bg-gray-50 ${
        isSelected ? 'bg-indigo-50' : ''
      }`}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        {conversation.otherUser.photoURL ? (
          <img
            src={conversation.otherUser.photoURL}
            alt={conversation.otherUser.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <span className="text-indigo-600 font-medium text-lg">
              {conversation.otherUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        {hasUnread && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span
            className={`font-medium truncate ${
              hasUnread ? 'text-gray-900' : 'text-gray-700'
            }`}
          >
            {conversation.otherUser.name}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
            {formatTime(conversation.lastMessage.timestamp)}
          </span>
        </div>
        <p
          className={`text-sm truncate ${
            hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
          }`}
        >
          {conversation.lastMessage.senderId === userId && (
            <span className="text-gray-400">Du: </span>
          )}
          {conversation.lastMessage.text || 'Keine Nachrichten'}
        </p>
      </div>
    </button>
  );
}

export default ConversationList;
